/**
 * CIPAA Details Page
 *
 * Tela de visualização de uma eleição CIPAA — segue a receita de
 * `EmployeeDetailsPage` (identidade → destaques → blocos temáticos).
 * As abas são mantidas: a skill autoriza abas explicitamente para `cipaa`.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as BackIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  HowToVote as VoteIcon,
  Assessment as ResultsIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  Bolt as ActionsIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  Gavel as TermIcon,
  Groups as GroupsIcon,
  Warning as RiskIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, IdentityBanner, IdentifierChip, SectionCard, InfoItem, StatCard, RouteActionButton } from '../../components';
import { pode } from '../../authorization/pode';
import { CipaaStatusChip, CipaaCandidatesManager } from './components';
import {
  Cipaa,
  CipaaStatus,
  formatDate,
  formatDateTime,
} from '../../types/cipaa';
import { cipaaService } from '../../services/cipaaService';
import { falhaDeCarga, textoDaFalhaDeCarga, type FalhaDeCarga } from '../../utils/acessoNegado';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const CipaaDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [election, setElection] = useState<Cipaa | null>(null);
  const [loading, setLoading] = useState(true);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Action dialogs
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<string>('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (id) {
      loadElection(parseInt(id, 10));
    }
  }, [id]);

  const loadElection = async (electionId: number) => {
    setLoading(true);
    setFalha(null);
    try {
      const data = await cipaaService.getById(electionId);
      setElection(data);
    } catch (err: unknown) {
      const falhaAoCarregar = falhaDeCarga(err, 'Não foi possível carregar a eleição. Tente de novo.');
      setFalha(falhaAoCarregar);
      // Releitura depois de uma ação: a eleição segue na tela e o aviso não aparece.
      if (election) {
        enqueueSnackbar(textoDaFalhaDeCarga(falhaAoCarregar, 'A eleição não foi encontrada.'), {
          variant: 'error',
        });
      } else if (falhaAoCarregar.status !== 403 && falhaAoCarregar.status !== 404) {
        enqueueSnackbar('Erro ao carregar eleição', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (type: string) => {
    setActionType(type);
    setActionDialogOpen(true);
  };

  const executeAction = async () => {
    if (!election) return;

    setActionLoading(true);
    try {
      switch (actionType) {
        case 'startRegistration':
          await cipaaService.startRegistration(election.id);
          enqueueSnackbar('Período de inscrições iniciado', { variant: 'success' });
          break;
        case 'closeRegistration':
          await cipaaService.closeRegistration(election.id);
          enqueueSnackbar('Período de inscrições encerrado', { variant: 'success' });
          break;
        case 'startVoting':
          await cipaaService.startVoting(election.id);
          enqueueSnackbar('Votação iniciada', { variant: 'success' });
          break;
        case 'closeVoting':
          await cipaaService.closeVoting(election.id);
          enqueueSnackbar('Votação encerrada', { variant: 'success' });
          break;
        case 'finalize':
          await cipaaService.finalize(election.id);
          enqueueSnackbar('Eleição finalizada', { variant: 'success' });
          break;
        case 'notifyUnion':
          await cipaaService.notifyUnion(election.id);
          // Generate and download the union notification PDF
          try {
            const blob = await cipaaService.downloadUnionNotificationPdf(election.id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `oficio-sindicato-${election.accessKey}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
          } catch (pdfErr) {
            console.error('Erro ao gerar PDF do ofício:', pdfErr);
          }
          enqueueSnackbar('Sindicato notificado - Ofício gerado', { variant: 'success' });
          break;
      }
      setActionDialogOpen(false);
      loadElection(election.id);
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao executar ação', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async (type: 'candidates' | 'elected' | 'attendance' | 'union-notification') => {
    if (!election) return;

    try {
      let response: Blob;
      let filename: string;

      switch (type) {
        case 'candidates':
          response = await cipaaService.downloadCandidatesPdf(election.id);
          filename = `candidatos-${election.accessKey}.pdf`;
          break;
        case 'elected':
          response = await cipaaService.downloadElectedPdf(election.id);
          filename = `eleitos-${election.accessKey}.pdf`;
          break;
        case 'attendance':
          response = await cipaaService.downloadAttendancePdf(election.id);
          filename = `lista-presença-${election.accessKey}.pdf`;
          break;
        case 'union-notification':
          response = await cipaaService.downloadUnionNotificationPdf(election.id);
          filename = `oficio-sindicato-${election.accessKey}.pdf`;
          break;
      }

      // response is already a Blob from the service
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('PDF gerado com sucesso', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao gerar PDF', { variant: 'error' });
    }
  };

  const handleCopyAccessKey = () => {
    if (!election) return;
    navigator.clipboard.writeText(election.accessKey);
    enqueueSnackbar('Chave de acesso copiada', { variant: 'info' });
  };

  const handleCopyVotingUrl = () => {
    if (!election) return;
    const url = `${window.location.origin}/votar/${election.accessKey}`;
    navigator.clipboard.writeText(url);
    enqueueSnackbar('Link de votação copiado', { variant: 'info' });
  };

  const handleCopyCandidatureUrl = () => {
    if (!election) return;
    const url = `${window.location.origin}/candidatura/eleicao/${election.accessKey}`;
    navigator.clipboard.writeText(url);
    enqueueSnackbar('Link de candidatura copiado', { variant: 'info' });
  };

  const getActionLabel = (type: string): string => {
    const labels: Record<string, string> = {
      startRegistration: 'Iniciar Inscrições',
      closeRegistration: 'Encerrar Inscrições',
      startVoting: 'Iniciar Votação',
      closeVoting: 'Encerrar Votação',
      finalize: 'Finalizar Eleição',
      notifyUnion: 'Notificar Sindicato',
    };
    return labels[type] || type;
  };

  const breadcrumbItems = [
    { label: 'Início' },
    { label: 'Segurança do Trabalho' },
    { label: 'Eleições CIPAA' },
    { label: election?.name || 'Carregando...' },
  ];

  const backButton = (
    <Button variant="outlined" size="small" startIcon={<BackIcon />} onClick={() => navigate('/cipaa')}>
      Voltar
    </Button>
  );

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbItems} actions={backButton}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!election) {
    return (
      <PageShell breadcrumbs={breadcrumbItems} actions={backButton}>
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Eleição não encontrada.')}
          rotuloDoBotao="Voltar para eleições"
          aoVoltar={() => navigate('/cipaa')}
        />
      </PageShell>
    );
  }

  const canEdit = [CipaaStatus.DRAFT, CipaaStatus.SCHEDULED].includes(election.status);
  const canStartRegistration = election.status === CipaaStatus.DRAFT;
  const canCloseRegistration = election.status === CipaaStatus.REGISTRATION_OPEN;
  const canStartVoting = election.status === CipaaStatus.REGISTRATION_CLOSED;
  const canCloseVoting = election.status === CipaaStatus.VOTING_OPEN;
  const canFinalize = election.status === CipaaStatus.VOTING_CLOSED;
  const canNotifyUnion = !election.unionNotificationDate && election.status !== CipaaStatus.CANCELLED;
  const canManageCandidates = [
    CipaaStatus.DRAFT,
    CipaaStatus.SCHEDULED,
    CipaaStatus.REGISTRATION_OPEN,
    CipaaStatus.REGISTRATION_CLOSED,
  ].includes(election.status);

  return (
    <PageShell
      breadcrumbs={breadcrumbItems}
      actions={
        <>
          {backButton}
          {canEdit && (
            <RouteActionButton
              para="/cipaa/:id/edit"
              exige={pode('cipaa.update')}
              aoAgir={() => navigate(`/cipaa/${election.id}/edit`)}
              variant="contained"
              size="small"
              startIcon={<EditIcon />}
            >
              Editar
            </RouteActionButton>
          )}
        </>
      }
    >
      {/* ═══ 1. Identidade ═══ */}
      <IdentityBanner
        avatar={<VoteIcon sx={{ fontSize: 32 }} />}
        name={election.name}
        color="#1565C0"
        status={<CipaaStatusChip status={election.status} />}
        context={election.company?.tradeName || election.company?.legalName || '—'}
        identifiers={
          <>
            <IdentifierChip icon={<KeyIcon sx={{ fontSize: 16 }} />} label={election.accessKey} />
            <Tooltip title="Copiar chave de acesso">
              <IconButton size="small" onClick={handleCopyAccessKey}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        }
      />

      {/* ═══ 2. Destaques — dimensionamento da CIPA ═══ */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<GroupsIcon fontSize="small" />}
            label="Funcionários"
            value={election.employeeCount != null ? String(election.employeeCount) : '—'}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<RiskIcon fontSize="small" />}
            label="Grau de Risco"
            value={election.riskLevel != null ? String(election.riskLevel) : '—'}
            color={theme.palette.warning.main}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<VoteIcon fontSize="small" />}
            label="Titulares"
            value={String(election.effectiveMembers)}
            color="#1565C0"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<VoteIcon fontSize="small" />}
            label="Suplentes"
            value={String(election.alternateMembers)}
            color={theme.palette.info.main}
          />
        </Grid>
      </Grid>

      {/* Status Actions */}
      <Box sx={{ mb: 3 }}>
        <SectionCard title="Ações" icon={<ActionsIcon fontSize="small" />}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {canStartRegistration && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<StartIcon />}
                onClick={() => handleAction('startRegistration')}
              >
                Iniciar Inscrições
              </Button>
            )}
            {canCloseRegistration && (
              <Button
                variant="contained"
                color="warning"
                startIcon={<StopIcon />}
                onClick={() => handleAction('closeRegistration')}
              >
                Encerrar Inscrições
              </Button>
            )}
            {canStartVoting && (
              <Button
                variant="contained"
                color="success"
                startIcon={<VoteIcon />}
                onClick={() => handleAction('startVoting')}
              >
                Iniciar Votação
              </Button>
            )}
            {canCloseVoting && (
              <Button
                variant="contained"
                color="warning"
                startIcon={<StopIcon />}
                onClick={() => handleAction('closeVoting')}
              >
                Encerrar Votação
              </Button>
            )}
            {canFinalize && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ResultsIcon />}
                onClick={() => handleAction('finalize')}
              >
                Finalizar e Apurar
              </Button>
            )}
            {canNotifyUnion && (
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => handleAction('notifyUnion')}
              >
                Notificar Sindicato
              </Button>
            )}

            <Divider orientation="vertical" flexItem />

            <Button
              variant="outlined"
              startIcon={<PdfIcon />}
              onClick={() => handleDownloadPdf('candidates')}
            >
              PDF Candidatos
            </Button>
            {election.status === CipaaStatus.COMPLETED && (
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={() => handleDownloadPdf('elected')}
              >
                PDF Eleitos
              </Button>
            )}
            {[CipaaStatus.VOTING_CLOSED, CipaaStatus.COMPLETED].includes(election.status) && (
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={() => handleDownloadPdf('attendance')}
              >
                Lista de Presença
              </Button>
            )}
            {election.unionNotificationDate && (
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={() => handleDownloadPdf('union-notification')}
              >
                Ofício Sindicato
              </Button>
            )}

            <Divider orientation="vertical" flexItem />

            {[CipaaStatus.REGISTRATION_OPEN, CipaaStatus.SCHEDULED].includes(election.status) && (
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={handleCopyCandidatureUrl}
              >
                Copiar Link Candidatura
              </Button>
            )}
            <Button variant="outlined" startIcon={<CopyIcon />} onClick={handleCopyVotingUrl}>
              Copiar Link Votação
            </Button>
          </Box>
        </SectionCard>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_e, v) => setTabValue(v)}>
          <Tab label="Detalhes" />
          <Tab label="Candidatos" />
          {election.status === CipaaStatus.COMPLETED && <Tab label="Resultados" />}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* ═══ 3. Blocos temáticos ═══ */}
        <Grid container spacing={2.5}>
          {/* General Info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Informações Gerais"
              icon={<BusinessIcon fontSize="small" />}
              accentColor={theme.palette.success.main}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Empresa"
                    value={election.company?.tradeName || election.company?.legalName}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem label="Responsável" value={election.responsible} />
                </Grid>
                <Grid size={12}>
                  <InfoItem
                    label="Local de Inscrição"
                    value={election.registrationLocation}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>

          {/* Voting Period */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Período de Votação"
              icon={<VoteIcon fontSize="small" />}
              accentColor={theme.palette.info.main}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Início"
                    value={formatDateTime(election.startDate, election.startTime)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Fim"
                    value={formatDateTime(election.endDate, election.endTime)}
                  />
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>

          {/* Important Dates */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Datas Importantes"
              icon={<EventIcon fontSize="small" />}
              accentColor={theme.palette.info.main}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Abertura Inscrições"
                    value={formatDate(election.registrationOpenDate ?? '')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Data da Eleição"
                    value={formatDate(election.electionDate ?? '')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Divulgação Candidatos"
                    value={formatDate(election.candidateAnnouncementDate ?? '')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Divulgação Resultados"
                    value={formatDate(election.resultsAnnouncementDate ?? '')}
                  />
                </Grid>
                <Grid size={12}>
                  <InfoItem
                    label="Notificação Sindicato"
                    value={
                      election.unionNotificationDate ? (
                        <Chip
                          label={formatDate(election.unionNotificationDate)}
                          size="small"
                          color="success"
                        />
                      ) : null
                    }
                  />
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>

          {/* Term Period */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Mandato"
              icon={<TermIcon fontSize="small" />}
              accentColor="#1565C0"
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem label="Início" value={formatDate(election.termStartDate ?? '')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem label="Fim" value={formatDate(election.termEndDate ?? '')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Estabilidade até"
                    value={formatDate(election.stabilityEndDate ?? '')}
                  />
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <CipaaCandidatesManager
          electionId={election.id}
          companyId={election.companyId}
          canManage={canManageCandidates}
        />
      </TabPanel>

      {election.status === CipaaStatus.COMPLETED && (
        <TabPanel value={tabValue} index={2}>
          <Typography color="text.secondary">
            Funcionalidade de resultados em desenvolvimento.
          </Typography>
        </TabPanel>
      )}

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)}>
        <DialogTitle>Confirmar Ação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja "{getActionLabel(actionType)}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button onClick={executeAction} variant="contained" disabled={actionLoading}>
            {actionLoading ? 'Processando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default CipaaDetailsPage;
